import React from 'react';
import { Box, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import type { SimulationActivity } from '../../../../types/simulation';
import { activityStopKindForUi } from '../../../../utils/simulation/inferActivityStopKind';
import { getPassengerCounts } from './DriverActivityDetailPanel';
import { getVehicleKey } from './useVehicleOperations';
import { resolveLegAddress } from './utils/addressResolution';

import { Icon } from '@liftango/ui-icons';
const TARMAC = '#2d2d38';
const ORANGE = '#EE7334';
const FONT = '"Montserrat", sans-serif';

function isPickupStop(activity: SimulationActivity): boolean {
  return activityStopKindForUi(activity) === 'pickup';
}

function isDropoffStop(activity: SimulationActivity): boolean {
  return activityStopKindForUi(activity) === 'dropoff';
}

function normalizeId(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const s = String(value).trim();
  return s.length > 0 ? s : null;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function formatActivityCoords(activity: SimulationActivity | null): string {
  if (!activity) return '—';
  const r = activity as Record<string, unknown>;
  const lat = toFiniteNumber(activity.activity_latitude ?? activity.latitude ?? r.lat);
  const lng = toFiniteNumber(activity.activity_longitude ?? activity.longitude ?? r.lng ?? r.lon);
  if (lat != null && lng != null) {
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }
  return '—';
}

/** Great-circle distance in metres (O–D vs snapped stop coords can differ more than a few metres). */
function haversineMeters(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371000;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLon = ((bLng - aLng) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

/** O–D endpoints vs snapped stop coordinates can differ by a few hundred metres. */
const COORD_MATCH_MAX_M = 450;

function coordsMatch(aLat: number, aLng: number, bLat: number, bLng: number): boolean {
  return haversineMeters(aLat, aLng, bLat, bLng) <= COORD_MATCH_MAX_M;
}

function isPlausibleLatLng(lat: number, lng: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  if (Math.abs(lat) < 1e-5 && Math.abs(lng) < 1e-5) return false;
  return true;
}

function getActivityLatLng(activity: SimulationActivity | null): { lat: number; lng: number } | null {
  if (!activity) return null;
  const r = activity as Record<string, unknown>;
  const lat = toFiniteNumber(
    activity.activity_latitude ?? activity.latitude ?? r.lat ?? r.from_latitude ?? r.pickup_latitude
  );
  const lng = toFiniteNumber(
    activity.activity_longitude ??
      activity.longitude ??
      r.lng ??
      r.lon ??
      r.from_longitude ??
      r.pickup_longitude ??
      r.dropoff_longitude
  );
  if (lat == null || lng == null || !isPlausibleLatLng(lat, lng)) return null;
  return { lat, lng };
}

function getRidePointPickupLatLng(p: Record<string, unknown>): { lat: number; lng: number } | null {
  const candidates: [unknown, unknown][] = [
    [p.from_latitude, p.from_longitude],
    [p.pickup_latitude, p.pickup_longitude],
    [p.origin_latitude, p.origin_longitude],
    [p.start_latitude, p.start_longitude],
  ];
  for (const [la, lo] of candidates) {
    const lat = toFiniteNumber(la);
    const lng = toFiniteNumber(lo);
    if (lat != null && lng != null && isPlausibleLatLng(lat, lng)) return { lat, lng };
  }
  const origin = p.origin;
  if (origin && typeof origin === 'object') {
    const o = origin as Record<string, unknown>;
    const lat = toFiniteNumber(o.latitude ?? o.lat);
    const lng = toFiniteNumber(o.longitude ?? o.lng ?? o.lon);
    if (lat != null && lng != null && isPlausibleLatLng(lat, lng)) return { lat, lng };
  }
  return null;
}

function getRidePointDropoffLatLng(p: Record<string, unknown>): { lat: number; lng: number } | null {
  const candidates: [unknown, unknown][] = [
    [p.to_latitude, p.to_longitude],
    [p.dropoff_latitude, p.dropoff_longitude],
    [p.destination_latitude, p.destination_longitude],
    [p.end_latitude, p.end_longitude],
  ];
  for (const [la, lo] of candidates) {
    const lat = toFiniteNumber(la);
    const lng = toFiniteNumber(lo);
    if (lat != null && lng != null && isPlausibleLatLng(lat, lng)) return { lat, lng };
  }
  const dest = p.destination;
  if (dest && typeof dest === 'object') {
    const d = dest as Record<string, unknown>;
    const lat = toFiniteNumber(d.latitude ?? d.lat);
    const lng = toFiniteNumber(d.longitude ?? d.lng ?? d.lon);
    if (lat != null && lng != null && isPlausibleLatLng(lat, lng)) return { lat, lng };
  }
  return null;
}

function formatLatLngPair(ll: { lat: number; lng: number } | null): string {
  if (!ll || !isPlausibleLatLng(ll.lat, ll.lng)) return '—';
  return `${ll.lat.toFixed(5)}, ${ll.lng.toFixed(5)}`;
}

const TRIP_ID_FIELDS = [
  'ride_id',
  'request_id',
  'driver_ride_id',
  'rider_ride_id',
  'external_reference_id',
  'external_user_reference_id',
  'booking_id',
  'trip_id',
  'requestId',
  'driverRideId',
  'riderRideId',
  'rideId',
  'externalReferenceId',
  'user_id',
  'userId',
  'rider_id',
  'passenger_id',
  'id',
] as const;

function pushTripKeysFromRecord(out: string[], rec: Record<string, unknown>): void {
  for (const k of TRIP_ID_FIELDS) {
    const s = normalizeId(rec[k]);
    if (s) out.push(s);
  }
}

/** Ride / request ids nested under users_picked_up, users_dropped_off (results activities API). */
function collectNestedTripKeysFromActivity(activity: SimulationActivity): string[] {
  const out: string[] = [];
  const raw = activity as Record<string, unknown>;
  const arrays = [raw.users_picked_up, raw.users_dropped_off, raw.passengers];
  for (const arr of arrays) {
    if (!Array.isArray(arr)) continue;
    for (const item of arr) {
      if (item != null && typeof item === 'object') {
        pushTripKeysFromRecord(out, item as Record<string, unknown>);
      }
    }
  }
  return out;
}

function collectNestedTripKeysFromRidePoint(p: Record<string, unknown>): string[] {
  const out: string[] = [];
  const arr = p.passengers;
  if (!Array.isArray(arr)) return out;
  for (const item of arr) {
    if (item != null && typeof item === 'object') {
      pushTripKeysFromRecord(out, item as Record<string, unknown>);
    }
  }
  return out;
}

function collectTripKeysFromActivity(activity: SimulationActivity): string[] {
  const r = activity as Record<string, unknown>;
  const raw = [
    activity.ride_id,
    activity.request_id,
    r.requestId,
    r.rider_ride_id,
    r.driver_ride_id,
    r.external_reference_id,
    r.externalReferenceId,
    r.external_user_reference_id,
    r.user_id,
    r.userId,
    r.rider_id,
    r.id,
  ]
    .map(normalizeId)
    .filter((x): x is string => x != null);
  return Array.from(new Set([...raw, ...collectNestedTripKeysFromActivity(activity)]));
}

function collectTripKeysFromRidePoint(p: Record<string, unknown>): string[] {
  const raw = [
    p.ride_id,
    p.request_id,
    p.external_reference_id,
    p.driver_ride_id,
    p.rider_ride_id,
    p.externalReferenceId,
    p.requestId,
    p.driverRideId,
    p.riderRideId,
    p.rideId,
    p.external_user_reference_id,
    p.user_id,
    p.userId,
    p.rider_id,
    p.id,
  ]
    .map(normalizeId)
    .filter((x): x is string => x != null);
  return Array.from(new Set([...raw, ...collectNestedTripKeysFromRidePoint(p)]));
}

function parseActivityTimeMs(activity: SimulationActivity): number | null {
  const fields = [
    activity.activity_start_time,
    activity.activity_start,
    activity.min_activity_start_time,
    activity.start_time,
    activity.time,
  ];
  for (const f of fields) {
    if (f && typeof f === 'string') {
      const p = Date.parse(f);
      if (!Number.isNaN(p)) return p;
    }
  }
  return null;
}

/**
 * Last pickup on the same vehicle strictly before the given drop (when ids don’t link rows).
 */
function findPickupOnVehicleBeforeDrop(
  activities: SimulationActivity[] | undefined,
  dropActivity: SimulationActivity
): SimulationActivity | null {
  if (!activities?.length) return null;
  const vKey = getVehicleKey(dropActivity);
  const dropMs = parseActivityTimeMs(dropActivity);
  if (dropMs == null) return null;
  let best: SimulationActivity | null = null;
  let bestMs = -Infinity;
  for (const a of activities) {
    if (getVehicleKey(a) !== vKey) continue;
    if (!isPickupStop(a)) continue;
    const t = parseActivityTimeMs(a);
    if (t == null || t >= dropMs) continue;
    if (t > bestMs) {
      bestMs = t;
      best = a;
    }
  }
  return best;
}

/**
 * First drop on the same vehicle strictly after the given pickup.
 */
function findDropOnVehicleAfterPickup(
  activities: SimulationActivity[] | undefined,
  pickupActivity: SimulationActivity
): SimulationActivity | null {
  if (!activities?.length) return null;
  const vKey = getVehicleKey(pickupActivity);
  const pickupMs = parseActivityTimeMs(pickupActivity);
  if (pickupMs == null) return null;
  let best: SimulationActivity | null = null;
  let bestMs = Infinity;
  for (const a of activities) {
    if (getVehicleKey(a) !== vKey) continue;
    if (!isDropoffStop(a)) continue;
    const t = parseActivityTimeMs(a);
    if (t == null || t <= pickupMs) continue;
    if (t < bestMs) {
      bestMs = t;
      best = a;
    }
  }
  return best;
}

function findPickupActivity(
  activities: SimulationActivity[] | undefined,
  tripKeys: string[]
): SimulationActivity | null {
  if (!activities?.length || tripKeys.length === 0) return null;
  const set = new Set(tripKeys);
  return (
    activities.find((a) => {
      if (!isPickupStop(a)) return false;
      return collectTripKeysFromActivity(a).some((k) => set.has(k));
    }) ?? null
  );
}

function findDropoffActivity(
  activities: SimulationActivity[] | undefined,
  tripKeys: string[]
): SimulationActivity | null {
  if (!activities?.length || tripKeys.length === 0) return null;
  const set = new Set(tripKeys);
  return (
    activities.find((a) => {
      if (!isDropoffStop(a)) return false;
      return collectTripKeysFromActivity(a).some((k) => set.has(k));
    }) ?? null
  );
}

function findPickupActivityByCoords(
  activities: SimulationActivity[] | undefined,
  lat: number,
  lng: number
): SimulationActivity | null {
  if (!activities?.length) return null;
  let best: SimulationActivity | null = null;
  let bestM = Infinity;
  for (const a of activities) {
    if (!isPickupStop(a)) continue;
    const c = getActivityLatLng(a);
    if (!c) continue;
    const m = haversineMeters(c.lat, c.lng, lat, lng);
    if (m <= COORD_MATCH_MAX_M && m < bestM) {
      bestM = m;
      best = a;
    }
  }
  return best;
}

function findDropoffActivityByCoords(
  activities: SimulationActivity[] | undefined,
  lat: number,
  lng: number
): SimulationActivity | null {
  if (!activities?.length) return null;
  let best: SimulationActivity | null = null;
  let bestM = Infinity;
  for (const a of activities) {
    if (!isDropoffStop(a)) continue;
    const c = getActivityLatLng(a);
    if (!c) continue;
    const m = haversineMeters(c.lat, c.lng, lat, lng);
    if (m <= COORD_MATCH_MAX_M && m < bestM) {
      bestM = m;
      best = a;
    }
  }
  return best;
}

function mergeTripKeys(...groups: (string[] | undefined)[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const g of groups) {
    if (!g?.length) continue;
    for (const k of g) {
      if (!seen.has(k)) {
        seen.add(k);
        out.push(k);
      }
    }
  }
  return out;
}

function passengerTotalFromRidePoint(p: Record<string, unknown>): number | null {
  const pass = p.passengers;
  if (Array.isArray(pass)) {
    let sum = 0;
    for (const x of pass) {
      if (x != null && typeof x === 'object') {
        const n = Number((x as Record<string, unknown>).number_of_passengers);
        sum += Number.isFinite(n) ? n : 0;
      }
    }
    if (sum > 0) return sum;
  }
  return null;
}

function formatPassengerSummary(
  activity: SimulationActivity,
  leg: 'pickup' | 'dropoff',
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const { standard, wheelchair } = getPassengerCounts(activity, leg);
  const total = standard + wheelchair;
  if (wheelchair > 0) {
    return t('results.point.passengerWithWheelchair', { total, standard, wheelchair });
  }
  return String(total);
}

export interface MapPointPopupContentProps {
  mode: 'activity' | 'ride_point';
  leg: 'pickup' | 'dropoff';
  activity?: SimulationActivity | null;
  ridePoint?: Record<string, unknown> | null;
  activities: SimulationActivity[] | undefined;
  vehicleKeyToLabel: Record<string, string>;
}

/**
 * Compact card for Leaflet popups on simulation map markers (matches PointDetails fields + pickup vehicle).
 */
const MapPointPopupContent: React.FC<MapPointPopupContentProps> = ({
  mode,
  leg,
  activity,
  ridePoint,
  activities,
  vehicleKeyToLabel,
}) => {
  const { t } = useTranslation('simulation');
  const baseTripKeys =
    mode === 'activity' && activity
      ? collectTripKeysFromActivity(activity)
      : ridePoint
        ? collectTripKeysFromRidePoint(ridePoint)
        : [];

  let tripKeys = baseTripKeys;
  const ridePointPickupLL =
    mode === 'ride_point' && ridePoint ? getRidePointPickupLatLng(ridePoint as Record<string, unknown>) : null;
  const ridePointDropLL =
    mode === 'ride_point' && ridePoint ? getRidePointDropoffLatLng(ridePoint as Record<string, unknown>) : null;

  let pickupActivity = findPickupActivity(activities, tripKeys);
  let dropActivityResolved: SimulationActivity | null = null;

  if (mode === 'ride_point' && ridePoint && activities?.length) {
    if (!pickupActivity && ridePointDropLL) {
      const dropAct = findDropoffActivityByCoords(activities, ridePointDropLL.lat, ridePointDropLL.lng);
      if (dropAct) {
        dropActivityResolved = dropAct;
        tripKeys = mergeTripKeys(tripKeys, collectTripKeysFromActivity(dropAct));
        pickupActivity = findPickupActivity(activities, tripKeys);
      }
    }
    if (!pickupActivity && ridePointPickupLL) {
      const p = findPickupActivityByCoords(activities, ridePointPickupLL.lat, ridePointPickupLL.lng);
      if (p) {
        pickupActivity = p;
        tripKeys = mergeTripKeys(tripKeys, collectTripKeysFromActivity(p));
      }
    }
    if (!pickupActivity && dropActivityResolved) {
      const chainPickup = findPickupOnVehicleBeforeDrop(activities, dropActivityResolved);
      if (chainPickup) {
        pickupActivity = chainPickup;
        tripKeys = mergeTripKeys(tripKeys, collectTripKeysFromActivity(chainPickup));
      }
    }
  }

  if (mode === 'activity' && activity && activities?.length && !pickupActivity) {
    if (isPickupStop(activity)) {
      pickupActivity = activity;
      tripKeys = mergeTripKeys(tripKeys, collectTripKeysFromActivity(activity));
    } else if (isDropoffStop(activity)) {
      const here = getActivityLatLng(activity);
      if (here) {
        const dropAct = findDropoffActivityByCoords(activities, here.lat, here.lng) ?? activity;
        dropActivityResolved = dropAct;
        tripKeys = mergeTripKeys(tripKeys, collectTripKeysFromActivity(dropAct));
        pickupActivity = findPickupActivity(activities, tripKeys);
        if (!pickupActivity) {
          const chainPickup = findPickupOnVehicleBeforeDrop(activities, dropAct);
          if (chainPickup) {
            pickupActivity = chainPickup;
            tripKeys = mergeTripKeys(tripKeys, collectTripKeysFromActivity(chainPickup));
          }
        }
      }
    }
  }

  const pickupVehicleKey = pickupActivity ? getVehicleKey(pickupActivity) : null;
  const pickupVehicleLabel =
    (pickupVehicleKey && vehicleKeyToLabel[pickupVehicleKey]) ||
    (pickupVehicleKey && pickupVehicleKey !== 'global' ? pickupVehicleKey : null);

  const primarySource: Record<string, unknown> | SimulationActivity =
    mode === 'activity' && activity ? activity : (ridePoint ?? {});

  let passengerLine: string;
  if (pickupActivity) {
    passengerLine = formatPassengerSummary(pickupActivity, 'pickup', t);
  } else if (mode === 'ride_point' && ridePoint) {
    const fromPoint = passengerTotalFromRidePoint(ridePoint);
    passengerLine = fromPoint != null ? String(fromPoint) : '—';
  } else if (activity) {
    passengerLine = formatPassengerSummary(activity, leg === 'pickup' ? 'pickup' : 'dropoff', t);
  } else {
    passengerLine = '—';
  }

  const requestTime =
    normalizeId((primarySource as Record<string, unknown>).request_time) ??
    normalizeId((primarySource as Record<string, unknown>).activity_start_time) ??
    normalizeId((primarySource as Record<string, unknown>).activity_start) ??
    normalizeId((primarySource as Record<string, unknown>).start_time) ??
    '—';

  let pickupCoords = '—';
  let dropCoords = '—';
  let resolvedDropActivity: SimulationActivity | null = dropActivityResolved;
  if (mode === 'ride_point' && ridePoint) {
    pickupCoords = formatLatLngPair(ridePointPickupLL);
    dropCoords = formatLatLngPair(ridePointDropLL);
    if (pickupCoords === '—' && pickupActivity) {
      pickupCoords = formatActivityCoords(pickupActivity);
    }
    if (dropCoords === '—' && activities?.length) {
      const dropAct =
        findDropoffActivity(activities, tripKeys) ??
        (ridePointDropLL
          ? findDropoffActivityByCoords(activities, ridePointDropLL.lat, ridePointDropLL.lng)
          : null);
      if (dropAct) {
        resolvedDropActivity = dropAct;
        dropCoords = formatActivityCoords(dropAct);
      }
    }
    if (pickupCoords === '—' && activities?.length && ridePointPickupLL) {
      const pickAct = findPickupActivityByCoords(activities, ridePointPickupLL.lat, ridePointPickupLL.lng);
      if (pickAct) pickupCoords = formatActivityCoords(pickAct);
    }
  } else if (activity) {
    const dropAct = activities ? findDropoffActivity(activities, tripKeys) : null;
    const fallbackDropFromVehicle =
      activities && pickupActivity ? findDropOnVehicleAfterPickup(activities, pickupActivity) : null;
    resolvedDropActivity = dropAct ?? fallbackDropFromVehicle;
    pickupCoords = formatActivityCoords(pickupActivity ?? (isPickupStop(activity) ? activity : null));
    dropCoords = formatActivityCoords(
      dropAct ?? fallbackDropFromVehicle ?? (isDropoffStop(activity) ? activity : null)
    );
  }

  const currentActivity = activity ?? null;
  const activityPickupSource = currentActivity && isPickupStop(currentActivity) ? currentActivity : null;
  const activityDropoffSource = currentActivity && isDropoffStop(currentActivity) ? currentActivity : null;

  const pickupAddress =
    resolveLegAddress({
      leg: 'pickup',
      ridePoint: ridePoint ?? undefined,
      activity: pickupActivity ?? activityPickupSource,
      fallbackToStopAddress: true,
      fallbackToCoords: true,
    }) ?? '—';
  const dropoffAddress =
    resolveLegAddress({
      leg: 'dropoff',
      ridePoint: ridePoint ?? undefined,
      activity: resolvedDropActivity ?? activityDropoffSource,
      fallbackToStopAddress: true,
      fallbackToCoords: true,
    }) ?? '—';

  const legLabel = leg === 'pickup' ? t('results.point.legPickup') : t('results.point.legDropoff');

  const ridePointVehicleRaw =
    mode === 'ride_point' && ridePoint
      ? normalizeId(
          (ridePoint as Record<string, unknown>).vehicle_id ??
            (ridePoint as Record<string, unknown>).vehicle_shift_id ??
            (ridePoint as Record<string, unknown>).shift_id
        )
      : null;
  const ridePointVehicleDisplay =
    ridePointVehicleRaw && ridePointVehicleRaw !== 'global'
      ? vehicleKeyToLabel[ridePointVehicleRaw] ?? ridePointVehicleRaw
      : null;

  const pickupVehicleDisplay =
    pickupVehicleLabel ??
    (pickupVehicleKey && pickupVehicleKey !== 'global' ? pickupVehicleKey : null) ??
    ridePointVehicleDisplay ??
    '—';

  const rows: { label: string; value: string; accent?: boolean }[] = [
    { label: t('results.point.stopLabel'), value: legLabel },
    { label: t('results.point.requestTime'), value: requestTime },
    { label: t('results.point.pickupAddress'), value: pickupAddress },
    { label: t('results.point.dropoffAddress'), value: dropoffAddress },
    { label: t('results.point.pickupCoords'), value: pickupCoords },
    { label: t('results.point.dropoffCoords'), value: dropCoords },
    { label: t('results.point.passengersLabel'), value: passengerLine },
    {
      label: t('results.point.pickupVehicle'),
      value: pickupVehicleDisplay,
      accent: true,
    },
  ];

  return (
    <Box
      sx={{
        fontFamily: FONT,
        color: '#fff',
        minWidth: 252,
        maxWidth: 300,
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        border: '1px solid rgba(0,0,0,0.08)',
        background: `linear-gradient(165deg, ${TARMAC} 0%, #1a1a22 100%)`,
      }}
    >
      <Box
        sx={{
          height: 3,
          background: `linear-gradient(90deg, ${ORANGE}, #ffb347)`,
        }}
      />
      <Box sx={{ px: 1.5, py: 0.85 }}>
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#9ca3af',
            mb: 0.45,
          }}
        >
          {t('results.point.tripDetails')}
        </Typography>
        {rows.map(({ label, value, accent }) => (
          <Box
            key={label}
            sx={{
              display: 'grid',
              gridTemplateColumns: accent ? '1fr' : 'auto 1fr',
              gap: accent ? 0.15 : 0.65,
              alignItems: accent ? 'flex-start' : 'baseline',
              mb: 0.35,
              pb: 0.35,
              borderBottom: '1px solid rgba(0,0,0,0.03)',
              '&:last-of-type': { borderBottom: 'none', mb: 0, pb: 0 },
            }}
          >
            {!accent && (
              <Typography
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: '#6b7280',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </Typography>
            )}
            {accent ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.1 }}>
                <Icon size={17} name="car-side" style={{ color: ORANGE, opacity: 0.95 }} />
                <Box>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#6b7280' }}>
                    {t('results.point.pickupVehicle')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                    {value}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography
                component="span"
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: '0.72rem',
                  color: '#fff',
                  wordBreak: 'break-word',
                  lineHeight: 1.28,
                }}
              >
                {value}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MapPointPopupContent;
